import React, { useContext, useMemo, useCallback, useEffect, useState } from 'react'
import { useTable, useSortBy, useFlexLayout, useFilters } from 'react-table'
import AutoSizer from 'react-virtualized-auto-sizer'
import { TableVirtuoso } from 'react-virtuoso'
import { WidgetDataContext } from '../../../wrappers/WidgetDataContext'
import Cell from './Cell'
import { CurrentDashboardContext } from '../../../wrappers/CurrentDashboardContext'
import { FaChevronUp, FaChevronDown, FaTimes } from 'react-icons/fa'
import stringFields from '../../../../utils/constants/widgets/stringFields'
import getHiddenColumns from '../../../../utils/widgets/getHiddenColumns'
import { formatDate } from '../../../../utils/valueFormatting'
import dateFields from '../../../../utils/constants/widgets/dateFields'
import dayjs from 'dayjs'
import DefaultSortTypeFunction, {
    customSortStationCode,
} from '../../../../utils/DefaultSortTypeFunction'
import DefaultHeaderCell from './DefaultHeaderCell'
import datetimeFields from '../../../../utils/constants/widgets/dateTimeFields'
import {
    operatorFilterFunction,
    getColumnFieldType,
    getDefaultOperatorForType,
} from '../../../../utils/filters/filterOperators'
import FilterOperatorSelect from '../../filters/FilterOperatorSelect'

const getFilterInputType = (fieldType, operator) => {
    if (fieldType === 'date') return operator === 'CONTAINS' ? 'text' : 'date'
    if (fieldType === 'number') return operator === 'CONTAINS' ? 'text' : 'number'
    return 'text'
}

const DefaultColumnFilter = ({
    column: { filterValue, preFilteredRows, setFilter, fieldType },
}) => {
    const count = preFilteredRows.length
    const initOperator =
        filterValue && typeof filterValue === 'object'
            ? filterValue.operator
            : getDefaultOperatorForType(fieldType)
    const initValue =
        filterValue && typeof filterValue === 'object'
            ? filterValue.value || ''
            : filterValue || ''
    const initValue2 =
        filterValue && typeof filterValue === 'object'
            ? filterValue.value2 || ''
            : ''

    const [operator, setOperator] = useState(initOperator)
    const [value, setValue] = useState(initValue)
    const [value2, setValue2] = useState(initValue2)

    const updateFilter = useCallback(
        (op, v1, v2) => {
            if (op !== 'BLANK' && !v1 && v1 !== 0) {
                setFilter(undefined)
                return
            }
            setFilter({
                operator: op,
                value: v1,
                value2: v2 || undefined,
                fieldType: fieldType || 'string',
            })
        },
        [setFilter, fieldType]
    )

    const onOperatorChange = useCallback(
        (op) => {
            setOperator(op)
            if (op === 'BLANK') {
                setValue('')
                setValue2('')
                updateFilter('BLANK', '', '')
                return
            }
            const isDate = (fieldType || 'string') === 'date'
            const switchedToDate = isDate && op !== 'CONTAINS' && operator === 'CONTAINS'
            const switchedFromDate = isDate && op === 'CONTAINS' && operator !== 'CONTAINS'
            const v1 = switchedToDate || switchedFromDate ? '' : value
            if (switchedToDate || switchedFromDate) setValue('')
            const newValue2 = op === 'BETWEEN' ? value2 : ''
            if (op !== 'BETWEEN') setValue2('')
            updateFilter(op, v1, newValue2)
        },
        [value, value2, operator, fieldType, updateFilter]
    )

    const onValueChange = useCallback(
        (e) => {
            const v = e.target.value
            setValue(v)
            updateFilter(operator, v, value2)
        },
        [operator, value2, updateFilter]
    )

    const onValue2Change = useCallback(
        (e) => {
            const v = e.target.value
            setValue2(v)
            updateFilter(operator, value, v)
        },
        [operator, value, updateFilter]
    )

    const clearFilter = useCallback(() => {
        setValue('')
        setValue2('')
        setFilter(undefined)
    }, [setFilter])

    useEffect(() => {
        if (!filterValue) {
            setValue('')
            setValue2('')
        }
    }, [filterValue, fieldType])

    const inputType = getFilterInputType(fieldType, operator)

    return (
        <>
            <FilterOperatorSelect
                fieldType={fieldType || 'string'}
                value={operator}
                onChange={onOperatorChange}
            />
            <div className="control is-expanded has-icons-right">
                <input
                    value={value}
                    onChange={onValueChange}
                    type={inputType}
                    disabled={operator === 'BLANK'}
                    className="input is-small"
                    placeholder={
                        inputType === 'date' ? '' : `Search ${count} records...`
                    }
                />
                <span
                    className="icon is-right"
                    style={{ pointerEvents: 'auto', cursor: 'pointer', color: '#9e9e9e' }}
                    onClick={clearFilter}
                >
                    <FaTimes size={12} />
                </span>
            </div>
            {operator === 'BETWEEN' && (
                <input
                    value={value2}
                    onChange={onValue2Change}
                    type={inputType}
                    className="input is-small filterBetweenInput"
                    placeholder={inputType === 'date' ? '' : 'End value...'}
                />
            )}
        </>
    )
}

//https://react-table-v7-docs.netlify.app/docs/api/useSortBy  ← Official Docs for version 7 of react table
// There are 3 different available sortTypes for the grids: ‘datetime', ‘basic’, 'alphanumeric’

// basic is the generic sort function if a > b then 1 else - 1 which works perfectly for numbers
// datetime is the same as basic but the values are parsed into dates with getTime() to convert the date to miliseconds since epoch
// alphanumeric is a custom function that sorts strings by dividing numbers from letter and from symbols
// Hard to explain with words… check ouit the docs
const createDateField = (col, isTime = false) => {
    const dateFormat = isTime ? 'MM/DD/YYYY HH:mm:ss' : 'MM/DD/YYYY'
    const INVALID_DATE = '01/01/1002'

    const formatCellValue = (value) => {
        if (!value) return ''
        
        const date = dayjs(value)
        if (!date.isValid()) return ''

        const formatted = date.format(dateFormat)
        return formatted.includes(INVALID_DATE) ? '' : formatted
    }

    return {
        Header: () => <DefaultHeaderCell title={col} />,
        id: col,
        accessor: (row) => formatDate(row[col], isTime),
        Cell: ({ cell }) => {
            const displayValue = formatCellValue(cell.value)
            return <Cell value={displayValue} type="string" />
        },
        Filter: DefaultColumnFilter,
        filter: operatorFilterFunction,
        fieldType: 'date',
        sortType: (a, b) =>
            dayjs(b.values[col]).toDate() - dayjs(a.values[col]).toDate(),
    }
}

const parseColumns = (data, valueLabel, sharedKey, widget) => {
    const exampleRow = data[0] // Get the first row to extract columns from
    const hiddenColumns = getHiddenColumns(widget)

    // Filter out unwanted columns
    const filteredColumns = Object.keys(exampleRow)
        .filter((col) => col !== 'Value') // Remove value column (added later with proper label)
        .filter((col) => !hiddenColumns.includes(col))
        .filter((col) => (sharedKey ? col !== sharedKey : true)) // Exclude shared key if present

    // Map columns to React Table format
    const columns = filteredColumns.map((col) => {
        const isStationCode = col === 'StationCode'
        const sortFunction = isStationCode
            ? customSortStationCode
            : DefaultSortTypeFunction(exampleRow, col, data)

        const isTime = datetimeFields.includes(col)
        const isDate = isTime || dateFields.includes(col)
        if (isDate) {
            return createDateField(col, isTime)
        }

        const fieldType = getColumnFieldType(col, data)

        return {
            Header: () => <DefaultHeaderCell title={col} />,
            accessor: col,
            Cell: ({ cell }) => (
                <Cell
                    value={cell.value}
                    type={
                        stringFields.includes(col) &&
                        !(cell.value && cell.value.toString().startsWith('<a'))
                            ? 'string'
                            : null
                    }
                />
            ),
            Filter: DefaultColumnFilter,
            filter: operatorFilterFunction,
            fieldType,
            sortType: sortFunction,
        }
    })

    // Add value column if it exists in the original data
    const hasValueColumn = Object.keys(exampleRow).includes('Value')
    if (hasValueColumn) {
        const valueColumn = {
            Header: () => <Cell value={valueLabel || 'Value'} type="string" />,
            accessor: 'Value',
            Cell: ({ cell }) => <Cell value={cell.value} />,
            Filter: DefaultColumnFilter,
            filter: operatorFilterFunction,
            fieldType: getColumnFieldType('Value', data),
        }
        return [...columns, valueColumn]
    }

    return columns
}

const Grid = () => {
    const {
        sharedPageKey,
        sharedPageId,
        setSharedPageId,
        globalWidgetState,
        setGlobalWidgetState,
    } = useContext(CurrentDashboardContext)
    const { widgetData: widget } = useContext(WidgetDataContext)
    const data = widget ? widget.Data : null
    const detailKey = widget ? widget.DetailKey : null
    const columns = useMemo(
        () => parseColumns(data, widget.ValueLabel, widget.SharedKey, widget),
        [data, widget]
    )
    const options =
        widget && widget.WidgetOptions && JSON.parse(widget.WidgetOptions)
    const sortOptions =
        (options && {
            sortBy: options.sortBy || [],
        }) ||
        {}

    const tableData = useMemo(() => data, [data])
    const defaultColumn = React.useMemo(
        () => ({
            minWidth: 30, // minWidth is only used as a limit for resizing
            width: 175, // width is used for both the flex-basis and flex-grow
            maxWidth: 300, // maxWidth is only used as a limit for resizing
        }),
        []
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state,
        setFilter,
    } = useTable(
        {
            columns,
            data: tableData,
            defaultColumn,
            initialState: {
                ...sortOptions,
            },
        },
        useFilters,
        useSortBy,
        useFlexLayout
    )

    useEffect(() => {
        if (globalWidgetState) {
            //WSW-223 allow widgets to keep there filter state when expanding the widget
            const currentWidgetGlobalStateId = Object.keys(
                globalWidgetState
            ).find((x) => x === String(widget.WidgetId))
            if (currentWidgetGlobalStateId) {
                const currentWidgetState =
                    globalWidgetState[currentWidgetGlobalStateId]
                const currentlyAppliedFilters = currentWidgetState.state.filters
                currentlyAppliedFilters.forEach(({ id, value }) => {
                    setFilter(id, value)
                })
            }
        }
    }, [])

    useEffect(() => {
        if (state && widget) {
            setGlobalWidgetState((prev) => ({
                ...prev,
                [widget.WidgetId]: { state, rows },
            }))
        }
    }, [state, widget, setGlobalWidgetState])

    const onClick = useCallback(
        (row) =>
            sharedPageKey &&
            detailKey !== sharedPageKey &&
            setSharedPageId(row.original[sharedPageKey]),
        [sharedPageKey, setSharedPageId, detailKey]
    )

    const components = useMemo(() => {
        return {
            Table: ({ style, ...props }) => (
                <table
                    key={'react-table'}
                    {...getTableProps()}
                    {...props}
                    style={{ ...style }}
                    className="table is-fullwidth is-striped"
                />
            ),
            TableBody: React.forwardRef(({ style, ...props }, ref) => (
                <tbody
                    {...getTableBodyProps()}
                    {...props}
                    ref={ref}
                    className="tbody"
                />
            )),
            TableRow: (props) => {
                const index = props['data-index']
                const row = props.context.rows[index]
                return (
                    <tr
                        {...props}
                        {...row.getRowProps()}
                        onClick={() => onClick(row)}
                        className={`tr ${
                            sharedPageKey &&
                            sharedPageId === row.original[sharedPageKey]
                                ? 'active'
                                : ''
                        } ${sharedPageKey ? 'pointer' : ''}`}
                    />
                )
            },
        }
    }, [sharedPageKey])

    return (
        <div className="gridWrapper gridWrapperScrollBar">
            <AutoSizer defaultHeight={300} disableWidth>
                {({ height }) => {
                    return (
                        <TableVirtuoso
                            context={{ rows }}
                            className="reactVirtuosoContainer"
                            style={{ height: height }}
                            data={rows}
                            totalCount={rows.length}
                            components={components}
                            fixedHeaderContent={() => {
                                return headerGroups.map(
                                    (headerGroup, index) => (
                                        <tr
                                            {...headerGroup.getHeaderGroupProps()}
                                            key={index}
                                            className="tr"
                                        >
                                            {headerGroup.headers.map(
                                                (column, i) => (
                                                    <th
                                                        {...column.getHeaderProps()}
                                                        key={i}
                                                    >
                                                        <div className="tableHeaderWrapper">
                                                            <div>
                                                                <span
                                                                    {...column.getSortByToggleProps(
                                                                        {
                                                                            className:
                                                                                'gridColumnHeader sticky th',
                                                                        }
                                                                    )}
                                                                >
                                                                    {column.render(
                                                                        'Header'
                                                                    )}
                                                                    <span className="gridColumnIcon">
                                                                        {column.isSorted ? (
                                                                            column.isSortedDesc ? (
                                                                                <FaChevronUp fontSize="0.75rem" />
                                                                            ) : (
                                                                                <FaChevronDown fontSize="0.75rem" />
                                                                            )
                                                                        ) : (
                                                                            ''
                                                                        )}
                                                                    </span>
                                                                </span>
                                                            </div>
                                                            <div className="tableFilterWrapper">
                                                                {column.canFilter
                                                                    ? column.render(
                                                                          'Filter'
                                                                      )
                                                                    : null}
                                                            </div>
                                                        </div>
                                                    </th>
                                                )
                                            )}
                                        </tr>
                                    )
                                )
                            }}
                            itemContent={(index) => {
                                const row = rows[index]
                                prepareRow(row)
                                return row.cells.map((cell) => (
                                    <td {...cell.getCellProps()} className="td">
                                        {cell.render('Cell')}
                                    </td>
                                ))
                            }}
                        />
                    )
                }}
            </AutoSizer>
        </div>
    )
}

export default Grid
